import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { useContent } from "@/hooks/use-content";
import { useCampaigns } from "@/hooks/use-campaigns";
import { useAgentLogs } from "@/hooks/use-agents";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useStudioHref } from "@/hooks/use-studio-href";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { PenTool, Target, MessageSquare, BrainCircuit, ClipboardCheck, BarChart3, Filter, Users, Zap, Play, CheckCircle2, XCircle, Clock, TrendingUp, Sparkles, AlertTriangle, Radar, ChevronDown, ChevronRight, CreditCard, ExternalLink } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import type { ContentItem, AdCampaign, Lead, AgentLog, AgentRun } from "@shared/schema";
import AgentThoughtStream from "@/components/agent-thought-stream";
import { usePilotMode } from "@/hooks/use-pilot-mode";

function CreditsBar() {
  const pilotMode = usePilotMode(); // PILOT_MODE: no "Get more" (billing) link
  const { data: credits, isLoading } = useQuery<{ balance: number; allowance: number; creditOverageCap: number | null }>({
    queryKey: ["/api/credits"],
    staleTime: 60000,
    refetchInterval: 120000,
  });

  if (isLoading || !credits) return null;

  const total = credits.allowance || 50;
  const remaining = Math.max(0, credits.balance);
  const pct = total > 0 ? (remaining / total) * 100 : 0;

  const colorClass = pct <= 5
    ? "bg-rose-500"
    : pct <= 20
    ? "bg-amber-500"
    : "bg-emerald-500";

  const textColorClass = pct <= 5
    ? "text-rose-400"
    : pct <= 20
    ? "text-amber-400"
    : "text-emerald-400";

  const borderColorClass = pct <= 5
    ? "border-rose-500/20"
    : pct <= 20
    ? "border-amber-500/20"
    : "border-emerald-500/20";

  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl bg-card/50 border ${borderColorClass}`} data-testid="credits-bar">
      <CreditCard size={14} className={textColorClass} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[11px] font-bold text-muted-foreground/70 uppercase tracking-wider">AI Credits</span>
          <span className={`text-[11px] font-bold ${textColorClass}`} data-testid="credits-remaining">{remaining.toLocaleString()} / {total.toLocaleString()}</span>
        </div>
        <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
          <div
            className={`h-full ${colorClass} transition-all duration-500 rounded-full`}
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </div>
      </div>
      {pct <= 20 && !pilotMode && (
        <Link href="/settings#billing" className="shrink-0 text-[11px] font-bold text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors" data-testid="link-get-credits">
          Get more <ExternalLink size={10} />
        </Link>
      )}
    </div>
  );
}

const cardAccents = [
  {
    gradient: "from-blue-500/[0.08] via-blue-600/[0.04] to-transparent",
    iconBg: "bg-blue-500/[0.12]",
    iconColor: "text-blue-400",
    glowColor: "shadow-[inset_0_1px_0_rgba(59,130,246,0.1),0_0_20px_rgba(59,130,246,0.05)]",
    borderColor: "border-blue-500/[0.08]",
  },
  {
    gradient: "from-purple-500/[0.08] via-purple-600/[0.04] to-transparent",
    iconBg: "bg-purple-500/[0.12]",
    iconColor: "text-purple-400",
    glowColor: "shadow-[inset_0_1px_0_rgba(168,85,247,0.1),0_0_20px_rgba(168,85,247,0.05)]",
    borderColor: "border-purple-500/[0.08]",
  },
  {
    gradient: "from-emerald-500/[0.08] via-emerald-600/[0.04] to-transparent",
    iconBg: "bg-emerald-500/[0.12]",
    iconColor: "text-emerald-400",
    glowColor: "shadow-[inset_0_1px_0_rgba(16,185,129,0.1),0_0_20px_rgba(16,185,129,0.05)]",
    borderColor: "border-emerald-500/[0.08]",
  },
  {
    gradient: "from-amber-500/[0.08] via-amber-600/[0.04] to-transparent",
    iconBg: "bg-amber-500/[0.12]",
    iconColor: "text-amber-400",
    glowColor: "shadow-[inset_0_1px_0_rgba(245,158,11,0.1),0_0_20px_rgba(245,158,11,0.05)]",
    borderColor: "border-amber-500/[0.08]",
  },
  {
    gradient: "from-rose-500/[0.08] via-rose-600/[0.04] to-transparent",
    iconBg: "bg-rose-500/[0.12]",
    iconColor: "text-rose-400",
    glowColor: "shadow-[inset_0_1px_0_rgba(244,63,94,0.1),0_0_20px_rgba(244,63,94,0.05)]",
    borderColor: "border-rose-500/[0.08]",
  },
];

const DATE_RANGES = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
  { label: "All time", value: 0 },
] as const;

const PLATFORM_FILTERS = ["All", "LinkedIn", "X", "Meta", "Google"] as const;

const platformColors: Record<string, { bg: string; text: string }> = {
  LinkedIn: { bg: "bg-blue-500", text: "text-blue-400" },
  X: { bg: "bg-foreground/60", text: "text-foreground/80" },
  Meta: { bg: "bg-indigo-500", text: "text-indigo-400" },
  Google: { bg: "bg-red-500", text: "text-red-400" },
  Instagram: { bg: "bg-pink-500", text: "text-pink-400" },
  Facebook: { bg: "bg-blue-600", text: "text-blue-400" },
  Twitter: { bg: "bg-sky-500", text: "text-sky-400" },
};

function getColor(platform: string) {
  return platformColors[platform] || { bg: "bg-foreground/40", text: "text-muted-foreground" };
}

function ExpandableContentList({ items }: { items: ContentItem[] }) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <>
      {items.map((item: ContentItem) => {
        const isExpanded = expandedId === item.id;
        const caption = item.caption || "Untitled";
        const isLong = caption.length > 80;
        return (
          <div
            key={item.id}
            className={`px-4 py-3.5 rounded-xl bg-muted/30 border border-border transition-colors duration-200 ${isLong ? "cursor-pointer hover:bg-muted/50" : ""}`}
            data-testid={`content-item-${item.id}`}
            onClick={() => isLong && setExpandedId(isExpanded ? null : item.id)}
            role={isLong ? "button" : undefined}
            tabIndex={isLong ? 0 : undefined}
            onKeyDown={isLong ? (e) => { if (e.key === "Enter" || e.key === " ") setExpandedId(isExpanded ? null : item.id); } : undefined}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className={`text-sm text-foreground/80 font-medium leading-relaxed ${isExpanded ? "" : "line-clamp-2"}`}>
                  {caption}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">{item.platform} · {item.type}</span>
                  {isLong && (
                    <span className="text-[10px] text-blue-400/60 flex items-center gap-0.5">
                      {isExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                      {isExpanded ? "less" : "more"}
                    </span>
                  )}
                </div>
              </div>
              <span
                className={`text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wide shrink-0 ${
                  item.status === "PUBLISHED"
                    ? "bg-emerald-500/[0.1] text-emerald-400 border border-emerald-500/[0.15]"
                    : item.status === "SCHEDULED"
                      ? "bg-amber-500/[0.1] text-amber-400 border border-amber-500/[0.15]"
                      : "bg-muted/50 text-muted-foreground/70 border border-border"
                }`}
              >
                {item.status}
              </span>
            </div>
          </div>
        );
      })}
    </>
  );
}

export default function Dashboard() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const studioHref = useStudioHref();
  const [, navigate] = useLocation();
  const { data: content, isLoading: loadingContent } = useContent();
  const { data: campaigns } = useCampaigns();

  const { data: plans, isLoading: loadingPlans } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "plans"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/plans`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 30000,
  });

  const hasActivePlan = Array.isArray(plans) && plans.some((p: any) => p.status === "ACTIVE");
  const plansReady = !loadingPlans && Array.isArray(plans);

  useEffect(() => {
    if (!activeWorkspaceId || !plansReady) return;
    if (!hasActivePlan) {
      navigate("/launch");
    }
  }, [activeWorkspaceId, plansReady, hasActivePlan]);

  const { data: workspaceStats, isLoading: loadingStats } = useQuery<{ totalContent: number; totalLeads: number; activeCampaigns: number; scheduledContent: number }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "stats"],
    queryFn: async () => {
      if (!activeWorkspaceId) return { totalContent: 0, totalLeads: 0, activeCampaigns: 0, scheduledContent: 0 };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/stats`, { credentials: "include" });
      if (!res.ok) return { totalContent: 0, totalLeads: 0, activeCampaigns: 0, scheduledContent: 0 };
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  });
  const { data: agentLogs, isLoading: loadingLogs } = useAgentLogs();

  const { data: agentRunsData } = useQuery<AgentRun[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "agent-runs"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/agent-runs`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const { data: eventLog } = useQuery<{ type: string; workspaceId: number; payload: Record<string, any>; timestamp: string; sourceAgent?: string }[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "events"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/events`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    refetchInterval: 30000,
  });

  const [dateRange, setDateRange] = useState<number>(30);
  const [platformFilter, setPlatformFilter] = useState<string>("All");

  const { data: leads } = useQuery<Lead[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "leads"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/leads`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch leads");
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const { data: opportunityData } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "opportunities"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/opportunities?status=new`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const { data: dailyBriefing } = useQuery<{ date: string; brandName: string; summary: Record<string, number>; highlights: string[]; actionItems: string[] }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "daily-briefing"],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/daily-briefing`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 5 * 60 * 1000,
  });

  // T006: Business intelligence summary
  const { data: intelligenceData } = useQuery<{ topObjections: { keyword: string; count: number }[]; topLeadSources: { source: string; count: number }[]; topPerformingContent: any[]; totalLeads30Days: number; hotLeadsCount: number; topPerformingAngle: string | null } | null>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "intelligence-summary"],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/intelligence-summary`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const { data: pendingAgentQuestions } = useQuery<{ id: number; agent: string; question: string; status: string }[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "agent-questions"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/agent-questions`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    refetchInterval: 10000,
  });

  const contentApprovalCount = useMemo(() => {
    if (!content) return 0;
    return content.filter((c: ContentItem) => c.approvalStatus === "pending_approval").length;
  }, [content]);

  const agentApprovalCount = useMemo(() => {
    if (!pendingAgentQuestions) return 0;
    return pendingAgentQuestions.filter((q) => q.status === "pending").length;
  }, [pendingAgentQuestions]);

  const approvalCount = contentApprovalCount + agentApprovalCount;

  const filteredContent = useMemo(() => {
    if (!content) return [];
    let items = content as ContentItem[];
    if (dateRange > 0) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - dateRange);
      items = items.filter((c) => c.createdAt && new Date(c.createdAt) >= cutoff);
    }
    if (platformFilter !== "All") {
      items = items.filter((c) => c.platform === platformFilter);
    }
    return items;
  }, [content, dateRange, platformFilter]);

  const filteredCampaigns = useMemo(() => {
    if (!campaigns) return [];
    let items = campaigns as AdCampaign[];
    if (dateRange > 0) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - dateRange);
      items = items.filter((c) => c.createdAt && new Date(c.createdAt) >= cutoff);
    }
    if (platformFilter !== "All") {
      items = items.filter((c) => c.platform === platformFilter);
    }
    return items;
  }, [campaigns, dateRange, platformFilter]);

  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    let items = leads as Lead[];
    if (dateRange > 0) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - dateRange);
      items = items.filter((l) => l.createdAt && new Date(l.createdAt) >= cutoff);
    }
    return items;
  }, [leads, dateRange]);

  const contentByPlatform = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredContent.forEach((c) => {
      counts[c.platform] = (counts[c.platform] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [filteredContent]);

  const leadFunnel = useMemo(() => {
    const stages = ["NEW", "CONTACTED", "QUALIFIED", "CONVERTED"];
    return stages.map((s) => ({
      label: s,
      count: filteredLeads.filter((l) => l.status === s).length,
    }));
  }, [filteredLeads]);

  const activeCampaigns = campaigns?.filter((c: AdCampaign) => c.status === "ACTIVE") || [];

  const dynamicHeadline = useMemo(() => {
    if (loadingContent || loadingStats) return null;
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const publishedThisWeek = content?.filter(
      (c: ContentItem) => c.status === "PUBLISHED" && (
        (c.publishedAt && new Date(c.publishedAt) >= weekAgo) ||
        (!c.publishedAt && c.createdAt && new Date(c.createdAt) >= weekAgo)
      )
    ).length ?? 0;
    const pendingApprovals = approvalCount;
    const radarHits = opportunityData?.length ?? 0;

    if (publishedThisWeek === 0 && pendingApprovals === 0 && radarHits === 0) {
      return "Your workspace is ready — run your agents to kick things off.";
    }
    const parts: string[] = [];
    if (publishedThisWeek > 0) {
      parts.push(`Your team published ${publishedThisWeek} piece${publishedThisWeek !== 1 ? "s" : ""} of content this week`);
    }
    if (pendingApprovals > 0) {
      parts.push(`${pendingApprovals} post${pendingApprovals !== 1 ? "s" : ""} waiting for your approval`);
    }
    if (radarHits > 0) {
      parts.push(`${radarHits} new radar hit${radarHits !== 1 ? "s" : ""} to review`);
    }
    if (parts.length === 1) return `${parts[0]}.`;
    if (parts.length === 2) return `${parts[0]}, with ${parts[1]}.`;
    return `${parts[0]}, with ${parts[1]} and ${parts[2]}.`;
  }, [content, approvalCount, opportunityData, loadingContent, loadingStats]);

  if (!activeWorkspaceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="no-workspace-state">
        <div className="w-16 h-16 rounded-2xl bg-card/50 border border-border flex items-center justify-center mb-5">
          <BrainCircuit size={28} className="text-muted-foreground/40" />
        </div>
        <h2 className="text-xl font-display font-bold text-muted-foreground">No Workspace Selected</h2>
        <p className="text-muted-foreground/60 mt-2 text-sm">Select or create a workspace to get started.</p>
      </div>
    );
  }

  const stats = [
    { title: "Content Items", value: workspaceStats?.totalContent ?? content?.length ?? 0, icon: PenTool, loading: loadingStats },
    { title: "Active Campaigns", value: workspaceStats?.activeCampaigns ?? activeCampaigns.length, icon: Target, loading: loadingStats },
    { title: "Total Leads", value: workspaceStats?.totalLeads ?? leads?.length ?? 0, icon: MessageSquare, loading: loadingStats },
    { title: "Radar Hits", value: opportunityData?.length || 0, icon: Radar, loading: false },
    { title: "Agent Actions", value: agentLogs?.length || 0, icon: BrainCircuit, loading: loadingLogs },
  ];

  const maxContentCount = Math.max(...contentByPlatform.map(([, v]) => v), 1);
  const maxCampaignBudget = Math.max(...(filteredCampaigns.map((c) => Math.max(c.budget, c.spent))), 1);
  const maxFunnelCount = Math.max(...leadFunnel.map((s) => s.count), 1);

  return (
    <div className="space-y-8 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          {dynamicHeadline ? (
            <p className="text-2xl md:text-3xl font-display font-black text-transparent bg-clip-text bg-gradient-to-r from-foreground via-foreground to-foreground/60 leading-tight" data-testid="dashboard-dynamic-headline">
              {dynamicHeadline}
            </p>
          ) : (
            <Skeleton className="h-9 w-80 bg-muted/50 rounded-lg" />
          )}
          <p className="text-muted-foreground/50 mt-1.5 text-xs font-medium uppercase tracking-wider">Command Center</p>
        </div>
      </div>

      <CreditsBar />

      <AgentThoughtStream />

      {dailyBriefing && (dailyBriefing.highlights?.length > 0 || dailyBriefing.actionItems?.length > 0) && (
        <div className="bg-gradient-to-br from-blue-500/[0.06] via-purple-500/[0.03] to-transparent border border-blue-500/[0.08] rounded-2xl p-5 mb-1" data-testid="daily-briefing-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-blue-400" />
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Daily Briefing</span>
            <span className="text-[9px] text-muted-foreground/50 ml-auto">{dailyBriefing.date}</span>
          </div>
          {dailyBriefing.highlights?.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {dailyBriefing.highlights.map((h, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                  <CheckCircle2 size={10} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span>{h}</span>
                </div>
              ))}
            </div>
          )}
          {dailyBriefing.actionItems?.length > 0 && (
            <div className="space-y-1.5">
              {dailyBriefing.actionItems.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] text-amber-400/70">
                  <Zap size={10} className="shrink-0" />
                  <span>{a}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(contentApprovalCount > 0 || agentApprovalCount > 0) && !loadingContent && (
        <div className="space-y-2">
          {contentApprovalCount > 0 && (
            <div
              className="flex items-center justify-between gap-4 p-4 bg-amber-500/[0.06] border border-amber-500/20 rounded-2xl cursor-pointer hover:bg-amber-500/[0.1] transition-all"
              data-testid="dashboard-pending-approval-banner"
              onClick={() => navigate(studioHref("review"))}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(studioHref("review")); }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/[0.12] flex items-center justify-center shrink-0">
                  <AlertTriangle size={16} className="text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-400">Action needed — {contentApprovalCount} post{contentApprovalCount !== 1 ? "s" : ""} awaiting your approval</p>
                  <p className="text-xs text-muted-foreground/60">Peggy created content for your calendar but it won't publish until you approve it.</p>
                </div>
              </div>
              <span className="text-xs font-bold text-amber-400 shrink-0">Review →</span>
            </div>
          )}
          {agentApprovalCount > 0 && (
            <div
              className="flex items-center justify-between gap-4 p-4 bg-blue-500/[0.06] border border-blue-500/20 rounded-2xl cursor-pointer hover:bg-blue-500/[0.1] transition-all"
              data-testid="dashboard-pending-agent-questions-banner"
              onClick={() => {
                const el = document.querySelector("[data-testid='agent-thought-stream']");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  const el = document.querySelector("[data-testid='agent-thought-stream']");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/[0.12] flex items-center justify-center shrink-0">
                  <MessageSquare size={16} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-blue-400">{agentApprovalCount} agent question{agentApprovalCount !== 1 ? "s" : ""} waiting for your input</p>
                  <p className="text-xs text-muted-foreground/60">Your agents are paused and need your direction to continue.</p>
                </div>
              </div>
              <span className="text-xs font-bold text-blue-400 shrink-0">Answer →</span>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-testid="stats-grid">
        {stats.map((stat, index) => {
          const accent = cardAccents[index];
          return (
            <div
              key={stat.title}
              className={`relative bg-gradient-to-br ${accent.gradient} border ${accent.borderColor} rounded-2xl p-5 ${accent.glowColor} backdrop-blur-sm transition-all duration-300 group`}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="absolute inset-0 rounded-2xl bg-muted/30 pointer-events-none" />
              <div className="relative">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <span className="text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">{stat.title}</span>
                  <div className={`w-9 h-9 rounded-xl ${accent.iconBg} flex items-center justify-center`}>
                    <stat.icon size={17} className={accent.iconColor} />
                  </div>
                </div>
                {stat.loading ? (
                  <Skeleton className="h-9 w-20 bg-muted/50 rounded-lg" />
                ) : (
                  <span className="text-3xl font-bold text-foreground tracking-tight" data-testid={`stat-value-${stat.title.toLowerCase().replace(/\s/g, "-")}`}>
                    {stat.value}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        <div
          className={`relative rounded-2xl p-5 backdrop-blur-sm transition-all duration-300 group cursor-pointer ${
            approvalCount > 0
              ? "bg-gradient-to-br from-amber-500/[0.12] via-rose-500/[0.06] to-transparent border-2 border-amber-500/40 shadow-[inset_0_1px_0_rgba(245,158,11,0.2),0_0_24px_rgba(245,158,11,0.08)] hover:border-amber-400/60 hover:shadow-[inset_0_1px_0_rgba(245,158,11,0.3),0_0_30px_rgba(245,158,11,0.12)]"
              : `bg-gradient-to-br ${cardAccents[4].gradient} border ${cardAccents[4].borderColor} ${cardAccents[4].glowColor} opacity-60`
          }`}
          data-testid="stat-card-approval-queue"
          onClick={() => {
            if (contentApprovalCount > 0) {
              navigate(studioHref("pending"));
            } else if (agentApprovalCount > 0) {
              document.querySelector("[data-testid='agent-thought-stream']")?.scrollIntoView({ behavior: "smooth" });
            } else {
              navigate(studioHref("pending"));
            }
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              if (contentApprovalCount > 0) {
                navigate(studioHref("pending"));
              } else if (agentApprovalCount > 0) {
                document.querySelector("[data-testid='agent-thought-stream']")?.scrollIntoView({ behavior: "smooth" });
              } else {
                navigate(studioHref("pending"));
              }
            }
          }}
        >
          <div className="absolute inset-0 rounded-2xl bg-muted/30 pointer-events-none" />
          <div className="relative">
            <div className="flex items-center justify-between gap-2 mb-4">
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${approvalCount > 0 ? "text-amber-400/90" : "text-muted-foreground/70"}`}>Approval Queue</span>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${approvalCount > 0 ? "bg-amber-500/[0.2]" : cardAccents[4].iconBg}`}>
                <ClipboardCheck size={17} className={approvalCount > 0 ? "text-amber-400" : cardAccents[4].iconColor} />
              </div>
            </div>
            {loadingContent ? (
              <Skeleton className="h-9 w-20 bg-muted/50 rounded-lg" />
            ) : (
              <div className="flex items-end justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`text-3xl font-bold tracking-tight ${approvalCount > 0 ? "text-amber-300" : "text-foreground"}`} data-testid="stat-value-approval-queue">
                    {approvalCount}
                  </span>
                  {approvalCount > 0 && (
                    <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[9px] no-default-hover-elevate no-default-active-elevate" data-testid="badge-pending-approvals">
                      {approvalCount} pending
                    </Badge>
                  )}
                </div>
                {approvalCount > 0 && (
                  <span className="text-[10px] text-amber-400 font-bold uppercase tracking-wide mb-1 flex items-center gap-1" data-testid="link-review-now">
                    Review Now →
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-muted/30 to-transparent border border-border rounded-2xl p-4 md:p-6 backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]" data-testid="agent-activity-log">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-lg bg-blue-500/[0.1] flex items-center justify-center">
              <BrainCircuit size={15} className="text-blue-400" />
            </div>
            <h2 className="text-lg font-display font-bold text-foreground">Agent Activity</h2>
          </div>

          {agentRunsData && agentRunsData.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="agent-runs-section">
              <span className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider">Recent Runs</span>
              {agentRunsData.slice(0, 5).map((run: AgentRun) => {
                const statusIcon = run.status === "completed" ? <CheckCircle2 size={12} className="text-emerald-400" /> :
                  run.status === "failed" ? <XCircle size={12} className="text-red-400" /> :
                  run.status === "running" ? <Play size={12} className="text-blue-400 animate-pulse" /> :
                  <Clock size={12} className="text-muted-foreground/70" />;
                const statusColor = run.status === "completed" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                  run.status === "failed" ? "bg-red-500/10 text-red-400 border-red-500/20" :
                  run.status === "running" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" :
                  "bg-muted/50 text-muted-foreground border-border";
                return (
                  <div
                    key={run.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted/30 border border-border"
                    data-testid={`agent-run-${run.id}`}
                  >
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500/15 to-blue-500/10 flex items-center justify-center shrink-0">
                      {statusIcon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground/90">{run.agent}</span>
                        <Badge className={`text-[9px] ${statusColor} no-default-hover-elevate no-default-active-elevate`} data-testid={`badge-run-status-${run.id}`}>
                          {run.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-[10px] text-muted-foreground/60">{run.trigger}</span>
                        {(run.actionsCount ?? 0) > 0 && <span className="text-[10px] text-muted-foreground/60">{run.actionsCount} actions</span>}
                        {(run.totalCost ?? 0) > 0 && <span className="text-[10px] text-emerald-400/60 font-mono">${(run.totalCost ?? 0).toFixed(4)}</span>}
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground/40 shrink-0 font-medium" data-testid={`text-run-time-${run.id}`}>
                      {run.startedAt ? new Date(run.startedAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {eventLog && eventLog.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="event-log-section">
              <span className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider">Event Bus Activity</span>
              {eventLog.slice(-8).reverse().map((ev, i) => {
                const eventLabels: Record<string, { icon: typeof Zap; label: string; color: string }> = {
                  "lead.created": { icon: Users, label: "New Lead", color: "text-emerald-400" },
                  "lead.cold": { icon: AlertTriangle, label: "Cold Lead", color: "text-amber-400" },
                  "strategy.approved": { icon: CheckCircle2, label: "Strategy Approved", color: "text-blue-400" },
                  "content.approved": { icon: CheckCircle2, label: "Content Approved", color: "text-purple-400" },
                  "content.rejected": { icon: XCircle, label: "Content Rejected", color: "text-red-400" },
                  "calendar.gap_detected": { icon: Clock, label: "Calendar Gap", color: "text-amber-400" },
                  "campaign.underperforming": { icon: TrendingUp, label: "Campaign Alert", color: "text-red-400" },
                  "agent.completed": { icon: CheckCircle2, label: "Agent Done", color: "text-emerald-400" },
                  "agent.failed": { icon: XCircle, label: "Agent Failed", color: "text-red-400" },
                };
                const info = eventLabels[ev.type] || { icon: Zap, label: ev.type, color: "text-muted-foreground" };
                const Icon = info.icon;
                return (
                  <div key={`ev-${i}`} className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-muted/30 border border-border" data-testid={`event-${i}`}>
                    <Icon size={14} className={info.color} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold ${info.color}`}>{info.label}</span>
                        {ev.sourceAgent && <span className="text-[9px] text-muted-foreground/50">{ev.sourceAgent}</span>}
                      </div>
                      <p className="text-[10px] text-muted-foreground/60 truncate">{ev.payload?.leadName || ev.payload?.title || ev.payload?.reason || ev.type}</p>
                    </div>
                    <span className="text-[9px] text-muted-foreground/40 shrink-0">
                      {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {loadingLogs ? (
            <div className="space-y-3">
              {Array(5).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full bg-card/50 rounded-xl" />
              ))}
            </div>
          ) : agentLogs && agentLogs.length > 0 ? (
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider">Activity Log</span>
              {(() => {
                const deduplicated: { log: AgentLog; count: number }[] = [];
                agentLogs.slice(0, 50).forEach((log: AgentLog) => {
                  const last = deduplicated[deduplicated.length - 1];
                  if (last && last.log.agent === log.agent && last.log.action === log.action) {
                    last.count++;
                  } else {
                    deduplicated.push({ log, count: 1 });
                  }
                });
                return deduplicated.slice(0, 10).map(({ log, count }) => (
                  <div
                    key={log.id}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-muted/30 border border-border hover-elevate transition-colors duration-200"
                    data-testid={`agent-log-${log.id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/15 to-purple-500/10 flex items-center justify-center shrink-0">
                      <BrainCircuit size={14} className="text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-foreground/90">{log.agent}</span>
                      <p className="text-xs text-muted-foreground/70 truncate mt-0.5">{log.action}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {count > 1 && (
                        <Badge className="bg-muted/60 text-muted-foreground/70 border border-border text-[9px] no-default-hover-elevate no-default-active-elevate" data-testid={`badge-log-count-${log.id}`}>
                          ×{count}
                        </Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground/40 font-medium">
                        {log.createdAt ? new Date(log.createdAt).toLocaleTimeString() : ""}
                      </span>
                    </div>
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <BrainCircuit size={24} className="text-muted-foreground/30 mb-3" />
              {(workspaceStats?.totalContent ?? content?.length ?? 0) > 0 ? (
                <>
                  <p className="text-foreground/80 text-sm font-medium" data-testid="text-setup-context">
                    Draper set up your workspace with {workspaceStats?.totalContent ?? content?.length ?? 0} content items.
                  </p>
                  <p className="text-muted-foreground/60 text-xs mt-1">
                    Run agents to keep the content engine running autonomously.
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground/60 text-sm">No agent activity yet.</p>
              )}
              <Link href="/strategy">
                <button className="mt-3 text-xs text-blue-400 hover:text-blue-300 transition-colors" data-testid="cta-run-strategy">
                  Run your first analysis in the Strategy Hub
                </button>
              </Link>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-muted/30 to-transparent border border-border rounded-2xl p-4 md:p-6 backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]" data-testid="recent-content">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/[0.1] flex items-center justify-center">
              <PenTool size={15} className="text-emerald-400" />
            </div>
            <h2 className="text-lg font-display font-bold text-foreground">Recent Content</h2>
          </div>
          {loadingContent ? (
            <div className="space-y-3">
              {Array(5).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full bg-card/50 rounded-xl" />
              ))}
            </div>
          ) : content && content.length > 0 ? (
            <div className="space-y-2">
              <ExpandableContentList items={content.slice(0, 8)} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <PenTool size={24} className="text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground/60 text-sm">No content created yet.</p>
              <Link href="/content">
                <button className="mt-3 text-xs text-blue-400 hover:text-blue-300 transition-colors" data-testid="cta-create-first-post">
                  Create your first post
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-6" data-testid="consolidated-analytics">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-500/[0.1] flex items-center justify-center">
              <BarChart3 size={15} className="text-purple-400" />
            </div>
            <h2 className="text-xl font-display font-bold text-foreground">Consolidated Analytics</h2>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <div className="flex items-center gap-1 md:gap-1.5 bg-card/50 border border-border rounded-xl p-1 overflow-x-auto" data-testid="date-range-filter">
              {DATE_RANGES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setDateRange(r.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    dateRange === r.value
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground/70 hover:text-muted-foreground"
                  }`}
                  data-testid={`filter-date-${r.value}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 md:gap-1.5 bg-card/50 border border-border rounded-xl p-1 overflow-x-auto" data-testid="platform-filter">
              <Filter size={12} className="text-muted-foreground/60 ml-2" />
              {PLATFORM_FILTERS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPlatformFilter(p)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    platformFilter === p
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground/70 hover:text-muted-foreground"
                  }`}
                  data-testid={`filter-platform-${p.toLowerCase()}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-muted/30 to-transparent border border-border rounded-2xl p-4 md:p-6 backdrop-blur-sm" data-testid="content-output-chart">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/[0.1] flex items-center justify-center">
                <PenTool size={15} className="text-emerald-400" />
              </div>
              <h3 className="text-lg font-display font-bold text-foreground">Content Output</h3>
              <span className="text-xs text-muted-foreground/60 ml-auto">{filteredContent.length} items</span>
            </div>
            {contentByPlatform.length > 0 ? (
              <div className="space-y-3">
                {contentByPlatform.map(([platform, count]) => {
                  const color = getColor(platform);
                  return (
                    <div key={platform} className="flex items-center gap-3" data-testid={`chart-bar-content-${platform.toLowerCase()}`}>
                      <span className="text-xs text-muted-foreground w-20 shrink-0 text-right font-medium">{platform}</span>
                      <div className="flex-1 h-7 bg-card/50 rounded-lg overflow-hidden">
                        <div
                          className={`h-full ${color.bg} rounded-lg transition-all duration-500 flex items-center justify-end px-2`}
                          style={{ width: `${Math.max((count / maxContentCount) * 100, 8)}%`, opacity: 0.7 }}
                        >
                          <span className="text-[10px] font-bold text-foreground/90">{count}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10">
                <PenTool size={20} className="text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground/60 text-xs">No content data for this period.</p>
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-muted/30 to-transparent border border-border rounded-2xl p-4 md:p-6 backdrop-blur-sm" data-testid="campaign-spend-chart">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg bg-amber-500/[0.1] flex items-center justify-center">
                <Target size={15} className="text-amber-400" />
              </div>
              <h3 className="text-lg font-display font-bold text-foreground">Campaign Spend</h3>
              <span className="text-xs text-muted-foreground/60 ml-auto">{filteredCampaigns.length} campaigns</span>
            </div>
            {filteredCampaigns.length > 0 ? (
              <div className="space-y-4">
                {filteredCampaigns.slice(0, 8).map((campaign) => (
                  <div key={campaign.id} className="space-y-1.5" data-testid={`chart-bar-campaign-${campaign.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-muted-foreground font-medium truncate max-w-[60%]">{campaign.name}</span>
                      <span className="text-[10px] text-muted-foreground/60 shrink-0">{campaign.platform}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground/70 w-12 shrink-0 text-right">Budget</span>
                      <div className="flex-1 h-5 bg-card/50 rounded-md overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-md transition-all duration-500"
                          style={{ width: `${Math.max((campaign.budget / maxCampaignBudget) * 100, 4)}%`, opacity: 0.5 }}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground w-14 shrink-0 font-medium">${campaign.budget.toFixed(0)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground/70 w-12 shrink-0 text-right">Spent</span>
                      <div className="flex-1 h-5 bg-card/50 rounded-md overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-md transition-all duration-500"
                          style={{ width: `${Math.max((campaign.spent / maxCampaignBudget) * 100, 4)}%`, opacity: 0.6 }}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground w-14 shrink-0 font-medium">${campaign.spent.toFixed(0)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10">
                <Target size={20} className="text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground/60 text-xs">No campaign data for this period.</p>
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-muted/30 to-transparent border border-border rounded-2xl p-4 md:p-6 backdrop-blur-sm" data-testid="lead-conversion-funnel">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg bg-blue-500/[0.1] flex items-center justify-center">
                <Users size={15} className="text-blue-400" />
              </div>
              <h3 className="text-lg font-display font-bold text-foreground">Lead Conversion Funnel</h3>
              <span className="text-xs text-muted-foreground/60 ml-auto">{filteredLeads.length} leads</span>
            </div>
            <div className="space-y-3">
              {leadFunnel.map((stage, i) => {
                const funnelColors = [
                  "bg-blue-500",
                  "bg-indigo-500",
                  "bg-purple-500",
                  "bg-emerald-500",
                ];
                const widthPct = maxFunnelCount > 0 ? Math.max((stage.count / maxFunnelCount) * 100, 8) : 8;
                return (
                  <div key={stage.label} className="flex items-center gap-3" data-testid={`funnel-stage-${stage.label.toLowerCase()}`}>
                    <span className="text-xs text-muted-foreground w-24 shrink-0 text-right font-medium">{stage.label}</span>
                    <div className="flex-1 h-8 bg-card/50 rounded-lg overflow-hidden">
                      <div
                        className={`h-full ${funnelColors[i]} rounded-lg transition-all duration-500 flex items-center justify-end px-2.5`}
                        style={{ width: `${widthPct}%`, opacity: 0.6 }}
                      >
                        <span className="text-[11px] font-bold text-foreground/90">{stage.count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {leadFunnel.every((s) => s.count === 0) && (
              <div className="flex flex-col items-center justify-center py-6 mt-2">
                <Users size={20} className="text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground/60 text-xs">No lead data for this period.</p>
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-muted/30 to-transparent border border-border rounded-2xl p-4 md:p-6 backdrop-blur-sm" data-testid="ai-usage-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg bg-purple-500/[0.1] flex items-center justify-center">
                <Zap size={15} className="text-purple-400" />
              </div>
              <h3 className="text-lg font-display font-bold text-foreground">AI Usage</h3>
            </div>
            {loadingLogs ? (
              <Skeleton className="h-20 w-full bg-card/50 rounded-xl" />
            ) : (
              <div className="flex flex-col items-center justify-center py-6">
                <span className="text-5xl font-black text-foreground tracking-tight" data-testid="ai-usage-count">
                  {agentLogs?.length || 0}
                </span>
                <span className="text-xs text-muted-foreground/70 mt-2 font-medium uppercase tracking-wider">Total Agent Actions</span>
                {agentLogs && agentLogs.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                    {Object.entries(
                      agentLogs.reduce<Record<string, number>>((acc, log: AgentLog) => {
                        acc[log.agent] = (acc[log.agent] || 0) + 1;
                        return acc;
                      }, {})
                    )
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([agent, count]) => (
                        <span
                          key={agent}
                          className="text-[10px] font-semibold px-2.5 py-1 rounded-md bg-purple-500/[0.08] text-purple-400/80 border border-purple-500/[0.1]"
                          data-testid={`ai-agent-${agent.toLowerCase().replace(/\s/g, "-")}`}
                        >
                          {agent}: {count}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* T006: Business Intelligence Card */}
        <Collapsible defaultOpen={true}>
          <div className="bg-gradient-to-br from-violet-950/30 to-transparent border border-violet-500/15 rounded-2xl p-4 md:p-6" data-testid="intelligence-card">
            <CollapsibleTrigger className="w-full" data-testid="intelligence-toggle">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-violet-500/[0.1] flex items-center justify-center">
                  <Radar size={15} className="text-violet-400" />
                </div>
                <h3 className="text-lg font-display font-bold text-foreground">Business Intelligence</h3>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20 ml-auto">Draper Insights</span>
                <ChevronDown size={16} className="text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-4">
                {intelligenceData && (intelligenceData.topObjections?.length > 0 || intelligenceData.topLeadSources?.length > 0) ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {intelligenceData.topLeadSources?.length > 0 && (
                      <div className="space-y-2" data-testid="intelligence-lead-sources">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Top Lead Sources</p>
                        {intelligenceData.topLeadSources.slice(0, 4).map((s, i) => (
                          <div key={i} className="flex items-center justify-between gap-2">
                            <span className="text-xs text-foreground/80 capitalize truncate">{s.source.replace(/_/g, " ")}</span>
                            <span className="text-xs font-bold text-violet-400 shrink-0">{s.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {intelligenceData.topObjections?.length > 0 && (
                      <div className="space-y-2" data-testid="intelligence-objections">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Common Objections</p>
                        {intelligenceData.topObjections.slice(0, 4).map((o, i) => (
                          <div key={i} className="flex items-center justify-between gap-2">
                            <span className="text-xs text-foreground/80 capitalize">{o.keyword}</span>
                            <span className="text-xs font-bold text-amber-400 shrink-0">{o.count}×</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Lead Activity</p>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">New (30d)</span>
                          <span className="text-xs font-bold text-emerald-400">{intelligenceData.totalLeads30Days}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">Hot leads</span>
                          <span className="text-xs font-bold text-red-400">{intelligenceData.hotLeadsCount}</span>
                        </div>
                        {(intelligenceData as any).adInsights ? (
                          <div className="mt-2 p-2 rounded-lg bg-muted/30 border border-border">
                            <p className="text-[10px] text-muted-foreground/60 mb-0.5">Top ad angle</p>
                            <p className="text-xs font-semibold text-foreground">{(intelligenceData as any).adInsights.topPerformingAngle || "No CTR data yet"}</p>
                            {(intelligenceData as any).adInsights.topCTR && <p className="text-[10px] text-emerald-400 mt-0.5">CTR: {(intelligenceData as any).adInsights.topCTR}</p>}
                          </div>
                        ) : intelligenceData.topPerformingAngle ? (
                          <div className="mt-2 p-2 rounded-lg bg-muted/30 border border-border">
                            <p className="text-[10px] text-muted-foreground/60 mb-0.5">Top performing</p>
                            <p className="text-xs font-semibold text-foreground">{intelligenceData.topPerformingAngle}</p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6" data-testid="intelligence-empty-state">
                    <p className="text-sm text-muted-foreground/60">No intelligence data yet. As leads interact and campaigns run, Draper will surface insights here.</p>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      </div>
    </div>
  );
}
